/**
 * Xero Service (stub)
 * Syncs a paid deal to Xero as a contact + invoice.
 * Mocked implementation - ready for Xero OAuth API integration.
 * Non-blocking: failures do not stop deal progression.
 */

import { supabase } from '../lib/supabase';
import { assessCredit, gstComponent, GST_TREATMENT_LABEL } from '../lib/gstTreatment';
import { GstTreatment } from '../types';
import { ACQUIRED_STATUSES, isSoldStatus } from '../lib/dealLifecycle';
import { statusLabel } from '../lib/dealStatusService';

export interface XeroResult {
  success: boolean;
  message: string;
  details?: {
    xero_reference?: string;
  };
}

export const xeroService = {
  /**
   * Push a deal's acquisition to Xero.
   *
   * This used to fabricate `XERO-MOCK-<id>` and write it into
   * `invoices.xero_reference`, which is worse than doing nothing: the column is
   * how you tell whether something reached the accounting system, and it was
   * full of references that looked real and pointed at nothing.
   *
   * It now calls the same adapter the closing-stock journal uses, and records a
   * reference only when Xero actually returns one.
   */
  syncDealToXero: async (dealId: string): Promise<XeroResult> => {
    try {
      const { data: deal, error } = await supabase
        .from('deals')
        .select('id, invoice_id, seller_name, valuation_amount, gst_treatment, seller_type, acquired_date, paid_at, created_at')
        .eq('id', dealId)
        .maybeSingle();

      if (error) throw error;
      if (!deal) return { success: false, message: 'Deal not found for Xero sync' };

      let res: Response;
      try {
        res = await fetch('/api/xero/manual-journal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            narration: `Vehicle acquisition — ${(deal as any).seller_name || 'seller'}`,
            date: ((deal as any).acquired_date || (deal as any).paid_at || (deal as any).created_at || '').slice(0, 10),
            basExcluded: false,
            lines: [
              { accountCode: '631', lineAmount: Number((deal as any).valuation_amount) || 0, description: 'Vehicle inventory' },
              { accountCode: '800', lineAmount: -(Number((deal as any).valuation_amount) || 0), description: 'Accounts payable — seller' },
            ],
          }),
        });
      } catch (e) {
        return { success: false, message: e instanceof Error ? e.message : 'Could not reach Xero.' };
      }

      const type = res.headers.get('content-type') || '';
      if (!type.includes('application/json')) {
        return { success: false, message: 'Xero is not deployed on this environment.' };
      }

      const body: any = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success || !body?.journalId) {
        return { success: false, message: body?.error || `Xero sync failed (${res.status}).` };
      }

      if ((deal as any).invoice_id) {
        await supabase
          .from('invoices')
          .update({ xero_reference: body.journalId })
          .eq('id', (deal as any).invoice_id);
      }

      return {
        success: true,
        message: `Acquisition posted to Xero (${body.journalId})`,
        details: { xero_reference: body.journalId },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      return { success: false, message: `Xero sync failed: ${msg}` };
    }
  }
};

/* ------------------------------------------------------------------ *
 * Division 66 GST register (second-hand goods)
 *
 * Rewritten for §6.6 of Sales Workflow v2.0-NL.2.0. The previous register gave
 * every deal a notional credit of one eleventh of the purchase price, in the
 * year it was bought, with no ceiling. That was wrong in three ways, all of
 * which changed the number the accountant lodges:
 *
 *   - a vehicle bought on a tax invoice from a GST-registered seller is not a
 *     margin-scheme purchase; the credit is the GST on that invoice
 *   - a Division 66 notional credit is claimable only when the vehicle SELLS,
 *     and is capped at the GST payable on that sale
 *   - some acquisitions carry no credit at all
 *
 * The rule itself lives in src/lib/gstTreatment.ts so the month-end report and
 * landed-cost maths cannot drift away from it.
 *
 * Because a margin-scheme credit is attributable to the period of resale, the
 * register can no longer simply select deals by acquisition date: a car bought
 * in June and sold in August belongs to August's figures. It therefore reads
 * anything acquired up to the end of the period and filters on the attribution
 * date each row works out for itself.
 *
 * Field mapping (real schema):
 *   purchase price  = deals.valuation_amount   (the agreed buy price)
 *   purchase date   = deals.acquired_date, else paid_at, else created_at
 *   sale price      = invoices.total_amount    (realised, when invoiced)
 *                     else deals.sale_price / estimated_sale_price (projected)
 *   GST on sale     = invoices.gst_amount      (realised) else 1/11th of sale
 * ------------------------------------------------------------------ */

export interface GSTRegisterRow {
  deal_id: string;
  vehicle: string;
  vin: string;
  /** Same stock number the Stock Management screen shows, so the two tie up. */
  stock_number: string;
  seller_type: string;
  /** §4.4 treatment this row was assessed under. */
  gst_treatment: GstTreatment;
  gst_treatment_label: string;
  /** True where the treatment was backfilled from seller type and never confirmed. */
  treatment_inferred: boolean;
  purchase_price: number;
  /**
   * "Margin Scheme Credit" - the gross amount this period's credit is worked out
   * on, capped per vehicle at the lesser of purchase and sale. GST on Purchases
   * (1B) is exactly this divided by eleven, which is the relationship the
   * register states.
   */
  margin_scheme_credit: number;
  /**
   * "G1 (BAS)" - Total Sales: the full, uncapped sale price once the car sells,
   * null while it is in stock. A reporting figure only; nothing on the purchase
   * side is worked out from it.
   */
  g1_bas: number | null;
  /** One eleventh of the purchase price — what the old register always reported. */
  notional_credit: number;
  /** What is actually claimable in this period, after the cap. */
  div66_credit: number;
  /**
   * Which side of this row belongs to the period being reported.
   *
   * A vehicle bought from a GST-registered seller claims its credit in the period of
   * PURCHASE and makes its sale in the period of SALE, and those are often different
   * quarters. Before 14 September the whole row was placed by the credit alone, so such a
   * car reported its Total Sales (G1), its GST on Sales (1A) and its net GST in the quarter
   * it was BOUGHT, and vanished from the quarter it was SOLD. Winnie's Margin Scheme spec
   * section 3a says the opposite in as many words: "g1_bas keys off sold_date, not
   * claimed_in_period". Found by the 14 September spec audit; 104 of 157 vehicles on file
   * are on the treatment that exposed it.
   *
   * So a row now appears in a period if EITHER side falls in it, and each side is totalled
   * only where it belongs.
   */
  credit_in_period: boolean;
  sale_in_period: boolean;
  credit_capped: boolean;
  purchase_date: string; // YYYY-MM-DD
  /** 'purchase' or 'sale' — which event the credit is attributable to. */
  attributes_to: 'purchase' | 'sale' | null;
  attribution_date: string | null;
  sale_basis: 'realised' | 'projected' | 'none';
  sale_price: number | null
  /** The date the vehicle sold. Winnie asked for it as a column of its own. */
  sale_date: string | null;
  gst_on_sale: number | null;
  net_gst_payable: number | null;
  deal_status: string;
  note: string;
}

export interface GSTSummary {
  fiscal_year: number;
  fy_label: string;
  total_vehicles: number;
  vehicles_sold: number;
  vehicles_in_stock: number;
  total_purchases: number;
  total_credits_claimed: number;
  /** Credits sitting on unsold stock — not claimable yet. */
  total_credits_deferred: number;
  total_gst_on_sales: number;
  /**
   * Total Sales for the period - the sum of G1 (BAS) across the rows, which the spec
   * keys off the sold date rather than the period the credit is claimed in. For a
   * margin-scheme car those are the same period by definition; keeping them apart in
   * the code stops that coincidence from being relied on.
   */
  total_sales_g1: number;
  total_net_gst_payable: number;
  /** Rows whose treatment was inferred rather than confirmed by a person. */
  unconfirmed_treatments: number;
}

/**
 * The period the register covers.
 *
 * Winnie's review asked for a custom From/To period. Rather than bolt a second
 * code path on, everything now takes a `GSTPeriod`; a fiscal year is just a
 * preset that resolves to the same {from, to} shape.
 */
export interface GSTPeriod {
  /** Inclusive start, YYYY-MM-DD. */
  from: string;
  /** Inclusive end, YYYY-MM-DD. */
  to: string;
  /** Set when the period came from the fiscal-year preset. */
  fiscalYear?: number;
  label: string;
}

export function fyPeriod(fiscalYear: number): GSTPeriod {
  return {
    from: `${fiscalYear}-07-01`,
    to: `${fiscalYear + 1}-06-30`,
    fiscalYear,
    label: fyLabel(fiscalYear),
  };
}

const auDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export function customPeriod(from: string, to: string): GSTPeriod {
  return { from, to, label: `${auDate(from)} – ${auDate(to)}` };
}

/** Filename-safe slug for a period. */
export function periodSlug(period: GSTPeriod): string {
  return period.fiscalYear !== undefined
    ? `FY${period.fiscalYear}-${period.fiscalYear + 1}`
    : `${period.from}_to_${period.to}`;
}

/** Australian FY label from the start (July) year, e.g. 2025 → "FY2025/26 (1 Jul 2025 – 30 Jun 2026)". */
export function fyLabel(fiscalYear: number): string {
  const end = (fiscalYear + 1).toString().slice(-2);
  return `FY${fiscalYear}/${end} (1 Jul ${fiscalYear} – 30 Jun ${fiscalYear + 1})`;
}

/** Quote a CSV cell, escaping embedded quotes/commas/newlines. */
function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * A vehicle is on the register once the money has gone out. Before `paid`
 * nothing has been acquired; `cancelled` never was.
 */
// ACQUIRED_STATUSES now comes from ../lib/dealLifecycle, so this register and the
// P&L cannot disagree about which cars are on the books.

/**
 * A sale that has actually happened, as opposed to a price we are forecasting.
 *
 * The Division 66 credit attaches to the SALE, which is the rule as Winnie wrote
 * it. It used to attach only to a DMS tax invoice, and that was wrong: the
 * EasyCars load brought in cars that were genuinely sold - status, sale date and
 * sale price all present - with no invoice raised in the DMS, and every one of
 * them reported G1 and claimable as zero. A car still in stock carrying an
 * estimated sale price stays a forecast, and a forecast still carries no credit.
 */
// Which statuses mean sold lives in ../lib/dealLifecycle. The list kept here used to
// miss sold_in_transit and sold_delivered, so a car on either carried no credit.

export function hasSold(
  deal: { deal_status?: string | null },
  saleDate: string | null | undefined,
): boolean {
  return isSoldStatus(deal.deal_status) && Boolean(saleDate);
}

export const gstRegister = {
  /**
   * Build the Division 66 register rows for a fiscal year (July → June),
   * keyed on the deal's created_at (acquisition) date.
   */
  async generateRegister(period: GSTPeriod): Promise<GSTRegisterRow[]> {
    // Exclusive upper bound: the day after the inclusive `to`.
    const periodEndExclusive = new Date(
      new Date(`${period.to}T00:00:00.000Z`).getTime() + 86400000
    ).toISOString();

    // Anything acquired up to the end of the period is a candidate, because a
    // margin-scheme credit lands in the period of RESALE — a car bought two
    // years ago and sold this quarter belongs in this quarter's register.
    const { data: deals, error } = await supabase
      .from('deals')
      .select(
        'id, vehicle_year, vehicle_make, vehicle_model, vehicle_vin, ' +
          'autograb_year, autograb_make, autograb_model, autograb_vin, ' +
          'valuation_amount, estimated_sale_price, sale_price, sale_date, seller_type, deal_status, ' +
          'gst_treatment, gst_treatment_source, acquired_date, paid_at, invoice_id, created_at, ' +
          // stock_number was read further down but never selected, so every row on
          // this register fell back to the first six characters of the deal id.
          'stock_number'
      )
      .is('deleted_at', null)
      // Only vehicles Direct Auto has actually acquired. A deal sitting at
      // sourced or under_offer is a car nobody has bought yet — there is no
      // acquisition, so there is no credit to claim and none to defer either.
      // The old register had the same flaw; it was simply invisible, because
      // every row was lumped into one "credits claimed" total.
      .in('deal_status', [...ACQUIRED_STATUSES])
      .lt('created_at', periodEndExclusive)
      .order('created_at', { ascending: true });

    if (error) throw error;
    const dealRows = deals || [];
    if (dealRows.length === 0) return [];

    // Pull the linked invoices in one query (realised sale price + GST + date).
    const invoiceIds = dealRows
      .map((d: any) => d.invoice_id)
      .filter((id: string | null): id is string => !!id);

    const invoiceById = new Map<
      string,
      { total_amount: number; gst_amount: number; invoice_date: string | null }
    >();
    if (invoiceIds.length > 0) {
      const { data: invoices, error: invErr } = await supabase
        .from('invoices')
        .select('id, total_amount, gst_amount, invoice_date')
        .in('id', invoiceIds);
      if (invErr) throw invErr;
      (invoices || []).forEach((inv: any) => {
        invoiceById.set(inv.id, {
          total_amount: Number(inv.total_amount) || 0,
          gst_amount: Number(inv.gst_amount) || 0,
          invoice_date: inv.invoice_date || null,
        });
      });
    }

    const inPeriod = (iso: string | null) =>
      !!iso && iso.slice(0, 10) >= period.from && iso.slice(0, 10) <= period.to;

    const rows: GSTRegisterRow[] = [];

    for (const d of dealRows as any[]) {
      const year = d.vehicle_year ?? d.autograb_year ?? '';
      const make = d.vehicle_make ?? d.autograb_make ?? '';
      const model = d.vehicle_model ?? d.autograb_model ?? '';
      const vehicle = `${year} ${make} ${model}`.trim();
      const vin = d.vehicle_vin || d.autograb_vin || '';

      const purchasePrice = Number(d.valuation_amount) || 0;
      const purchaseDateIso = d.acquired_date || d.paid_at || d.created_at || null;
      const purchaseDate = purchaseDateIso ? String(purchaseDateIso).slice(0, 10) : '';

      const invoice = d.invoice_id ? invoiceById.get(d.invoice_id) : undefined;

      let saleBasis: GSTRegisterRow['sale_basis'] = 'none';
      let salePrice: number | null = null;
      let gstOnSaleRaw: number | null = null;
      let saleDate: string | null = null;

      if (invoice && invoice.total_amount > 0) {
        salePrice = invoice.total_amount;
        gstOnSaleRaw = invoice.gst_amount > 0 ? invoice.gst_amount : gstComponent(salePrice);
        saleDate = (d.sale_date || invoice.invoice_date || d.paid_at || null) as string | null;
        /*
         * This line used to read `saleBasis = 'realised'`, set above with no status
         * test at all, so an invoice raised before the vehicle left realised the
         * margin scheme credit on the strength of the paperwork alone. Winnie's
         * Margin Scheme spec section 3 is explicit: a NITC vehicle at
         * paid_ready_for_sale or deal_pending carries a credit of zero until it
         * sells. The other two branches below already asked. This one did not,
         * which is how a car still on the lot could claim a credit a quarter early.
         */
        saleBasis = hasSold(d, saleDate) ? 'realised' : 'projected';
      } else if (d.sale_price && Number(d.sale_price) > 0) {
        saleBasis = hasSold(d, d.sale_date) ? 'realised' : 'projected';
        salePrice = Number(d.sale_price);
        gstOnSaleRaw = gstComponent(salePrice);
        saleDate = d.sale_date || null;
      } else if (d.estimated_sale_price && Number(d.estimated_sale_price) > 0) {
        saleBasis = 'projected';
        salePrice = Number(d.estimated_sale_price);
        gstOnSaleRaw = gstComponent(salePrice);
        saleDate = d.sale_date || null;
      }

      // Only a sale that has happened can carry a credit. A projected sale price is shown
      // so the register reads like a forecast, but it must never move the number
      // that goes on the BAS.
      const assessment = assessCredit(d, purchasePrice, {
        salePrice: saleBasis === 'realised' ? salePrice : null,
        gstOnSale: saleBasis === 'realised' ? gstOnSaleRaw : null,
        saleDate,
      });

      /* Which period does each SIDE of this row belong to? See GSTRegisterRow.credit_in_period.
         The purchase side goes where the credit attaches; the sale side goes where the sale
         happened, which for a tax-invoice vehicle is a different quarter. */
      const creditInPeriod =
        assessment.attributesTo === 'sale'
          ? inPeriod(assessment.attributionDate)
          : inPeriod(purchaseDateIso);

      const saleInPeriod = saleBasis === 'realised' && inPeriod(saleDate);

      // Stock acquired in the period but not yet sold still belongs on the
      // register — it is the deferred credit the accountant needs to see.
      const deferredStock =
        assessment.attributesTo === null && inPeriod(purchaseDateIso);

      if (!creditInPeriod && !saleInPeriod && !deferredStock) continue;

      /* Spec section 3: gst_on_sales_1a = sold ? round(sale_price / 11, 2) : null.
         This was a ternary with the same expression on both sides - the raw
         GST returned whether the row was realised or not. It looked like a gate and
         was not one, so a projected row published a 1A figure on the table and in
         the CSV while getSummary below correctly left it out of the tile. The row
         and the total disagreed, and nothing in the code looked wrong. */
      const gstOnSale = saleBasis === 'realised' ? gstOnSaleRaw : null;
      const netGst =
        saleBasis === 'realised' && gstOnSale !== null
          ? Math.round((gstOnSale - assessment.claimable) * 100) / 100
          : null;

      rows.push({
        deal_id: d.id,
        vehicle,
        stock_number:
          d.stock_number != null ? String(d.stock_number) : d.id.slice(0, 6).toUpperCase(),
        vin,
        seller_type: d.seller_type || '',
        gst_treatment: assessment.treatment,
        gst_treatment_label: GST_TREATMENT_LABEL[assessment.treatment],
        treatment_inferred: (d.gst_treatment_source ?? 'inferred') === 'inferred',
        purchase_price: purchasePrice,
        margin_scheme_credit: assessment.marginSchemeCredit,
        g1_bas: assessment.g1Bas,
        notional_credit: assessment.notional,
        div66_credit: assessment.claimable,
        credit_in_period: creditInPeriod || deferredStock,
        sale_in_period: saleInPeriod,
        credit_capped: assessment.capped,
        purchase_date: purchaseDate,
        attributes_to: assessment.attributesTo,
        attribution_date: assessment.attributionDate
          ? String(assessment.attributionDate).slice(0, 10)
          : null,
        sale_basis: saleBasis,
        sale_price: salePrice,
        sale_date: saleDate,
        gst_on_sale: gstOnSale,
        net_gst_payable: netGst,
        deal_status: d.deal_status || '',
        note: assessment.note,
      });
    }

    /*
     * Return reversals (Winnie, 18 Sep).
     *
     * When a vehicle bought on a tax invoice goes back to the seller, the GST credit that was
     * claimed at purchase has to be unwound. Her instruction is precise about where it lands:
     * "posts automatically as a negative line into the Margin Scheme GST Register for the
     * CURRENT period... rather than silently editing the vehicle's original row - the original
     * purchase-period claim stays exactly as it was, correct and untouched".
     *
     * So it is a row of its own, in the period the RETURN happened, and the vehicle's original
     * row in its original period is left alone. A reader looking at the quarter the car was
     * bought in still sees exactly what was claimed and why.
     */
    try {
      const { data: reversals } = await supabase
        .from('gst_reversal_entries')
        .select('*')
        .order('period_date', { ascending: true });

      for (const raw of (reversals || []) as unknown as Record<string, unknown>[]) {
        const periodDate = String(raw.period_date || '').slice(0, 10);
        if (!inPeriod(periodDate)) continue;
        const amount = Number(raw.reversal_amount) || 0;
        rows.push({
          deal_id: String(raw.deal_id),
          vehicle: String(raw.label || 'Return — GST reversal'),
          stock_number: '—',
          vin: String(raw.vin || ''),
          seller_type: '',
          gst_treatment: 'tax_invoice',
          gst_treatment_label: GST_TREATMENT_LABEL['tax_invoice'],
          treatment_inferred: false,
          purchase_price: amount,
          margin_scheme_credit: amount,
          g1_bas: null,
          notional_credit: Number(raw.notional_credit_reversal) || 0,
          div66_credit: Number(raw.notional_credit_reversal) || 0,
          credit_in_period: true,
          sale_in_period: false,
          credit_capped: false,
          purchase_date: periodDate,
          attributes_to: 'purchase',
          attribution_date: periodDate,
          sale_basis: 'projected',
          sale_price: null,
          sale_date: null,
          gst_on_sale: null,
          net_gst_payable: null,
          deal_status: 'cancelled',
          note: 'Vehicle returned to the seller. This reverses the credit claimed when it was bought; the original purchase-period row is unchanged.',
        });
      }
    } catch (e) {
      /* A register that cannot read the reversals is still a useful register, and throwing
         here would take the whole BAS screen down over a table that is usually empty. */
      console.error('GST reversal entries could not be read', e);
    }

    rows.sort((a, b) =>
      (a.attribution_date || a.purchase_date).localeCompare(b.attribution_date || b.purchase_date)
    );
    return rows;
  },

  /**
   * Aggregate for the header tiles.
   *
   * "Credits claimed" is now what is actually claimable in this period. The
   * credit sitting on unsold stock is reported separately as deferred, because
   * conflating the two is exactly what overstated the old figure.
   */
  async getSummary(period: GSTPeriod): Promise<GSTSummary> {
    const rows = await this.generateRegister(period);
    let vehiclesSold = 0;
    let totalPurchases = 0;
    let totalCredits = 0;
    let totalDeferred = 0;
    let totalGstOnSales = 0;
    let totalSalesG1 = 0;
    let totalNetGst: number;
    let unconfirmed = 0;

    /* Each side is totalled in its own period. A tax-invoice vehicle bought in one quarter
       and sold in another contributes its purchase and its credit to the first, and its
       Total Sales, GST on Sales and net GST to the second. Totalling the whole row in one
       place is the defect the 14 September audit found. */
    rows.forEach((r) => {
      if (r.credit_in_period) {
        totalPurchases += r.purchase_price;
        totalCredits += r.div66_credit;
        if (r.attributes_to === null) totalDeferred += r.notional_credit;
        if (r.treatment_inferred) unconfirmed += 1;
      }
      if (r.sale_in_period) {
        vehiclesSold += 1;
        totalGstOnSales += r.gst_on_sale || 0;
        totalSalesG1 += r.g1_bas || 0;
      }
    });

    /*
     * Net GST for the PERIOD, not the sum of the per-vehicle figures.
     *
     * A row's own net GST is its 1A less its own credit, which is the right number for that
     * vehicle. Summing it across the period stopped being right the moment the two sides of
     * a row could fall in different periods: for a vehicle sold this period whose credit was
     * claimed last period, that row's credit would be relieved twice - once in the period it
     * was actually claimed, and again inside this period's net figure.
     *
     * What goes on the BAS is the period's 1A less the period's 1B, which is what these two
     * running totals already are.
     */
    totalNetGst = totalGstOnSales - totalCredits;

    const round2 = (n: number) => Math.round(n * 100) / 100;

    return {
      fiscal_year: period.fiscalYear ?? 0,
      fy_label: period.label,
      total_vehicles: rows.length,
      vehicles_sold: vehiclesSold,
      /* Rows on the register that are not a sale counted in this period: stock still on hand,
         plus any row here only because its credit attached in this period while its sale
         belongs to another. */
      vehicles_in_stock: rows.filter((r) => !r.sale_in_period).length,
      total_purchases: round2(totalPurchases),
      total_credits_claimed: round2(totalCredits),
      total_credits_deferred: round2(totalDeferred),
      total_gst_on_sales: round2(totalGstOnSales),
      total_sales_g1: round2(totalSalesG1),
      total_net_gst_payable: round2(totalNetGst),
      unconfirmed_treatments: unconfirmed,
    };
  },

  /** Build the CSV string (with UTF-8 BOM for Excel). */
  async exportCSV(period: GSTPeriod): Promise<string> {
    const rows = await this.generateRegister(period);

    /* Winnie's 10 Sep column order. The sales-side figures - Sale Price, Sold Date,
       G1 (BAS), GST on Sales (1A) - read together in one block, so nobody scans across a
       row and reads the purchase-side Margin Scheme Credit as if it were Total Sales.

       Gone from this export, deliberately: the old uncapped "Notional Credit (1/11)"
       column, which is the legacy figure the spec retires, along with Treatment
       Confirmed, Credit Capped, Attributable To, Sale Basis and Note. Say the word and
       any of them come back - they are all still on the row. */
    const headers = [
      'Vehicle',
      'Stock No',
      'VIN',
      'Seller',
      'GST Treatment',
      'Purchase',
      'Purchase Date',
      'Margin Scheme Credit',
      'GST on Purchases (1B)',
      'Claimed In',
      'Sale Price',
      'Sold Date',
      'G1 (BAS)',
      'GST on Sales (1A)',
      'Net GST Payable',
      'Status',
    ];

    const lines = rows.map((r) =>
      [
          csvCell(r.vehicle),
          csvCell(r.stock_number),
          csvCell(r.vin),
          csvCell(r.seller_type),
          csvCell(r.gst_treatment_label),
          csvCell(r.purchase_price),
          csvCell(r.purchase_date),
          csvCell(r.margin_scheme_credit),
          csvCell(r.div66_credit),
          csvCell(
            r.attributes_to
              ? `${r.attributes_to === 'sale' ? 'Sale' : 'Purchase'} · ${r.attribution_date ?? ''}`.trim()
              : 'not yet claimable'
          ),
          csvCell(r.sale_price ?? ''),
          csvCell(r.sale_date ?? ''),
          csvCell(r.g1_bas ?? ''),
          csvCell(r.gst_on_sale ?? ''),
          csvCell(r.net_gst_payable ?? ''),
          csvCell(statusLabel(r.deal_status)),
      ].join(',')
    );

    return '﻿' + [headers.join(','), ...lines].join('\r\n');
  },

  /** Trigger a browser download of the register CSV. */
  downloadCSV(csvContent: string, period: GSTPeriod): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DirectAuto_MarginScheme_GST_${periodSlug(period)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
